import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../services/api';
import { useApiState } from '../hooks/useApiState';
import './AuditLog.css';

function AuditLog() {
  const navigate = useNavigate();
  const { data: logs, loading, error, executeRequest } = useApiState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [selectedLog, setSelectedLog] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const filterLogs = useCallback(() => {
    let filtered = [...logs];

    // Filter by action type
    if (actionTypeFilter !== 'all') {
      filtered = filtered.filter(log => log.action_type === actionTypeFilter);
    }

    // Filter by search term (admin username, target username, or reason)
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(log =>
        (log.admin_username && log.admin_username.toLowerCase().includes(term)) ||
        (log.target_username && log.target_username.toLowerCase().includes(term)) ||
        (log.reason && log.reason.toLowerCase().includes(term)) ||
        (log.action_type && log.action_type.toLowerCase().includes(term))
      );
    }

    setFilteredLogs(filtered);
  }, [logs, searchTerm, actionTypeFilter]);

  useEffect(() => {
    loadAuditLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  useEffect(() => {
    filterLogs();
  }, [filterLogs]);

  const loadAuditLog = async () => {
    try {
      const offset = page * pageSize;
      await executeRequest(async () => {
        const response = await adminAPI.getAuditLog(pageSize, offset);
        setPagination(response.data.pagination || { total: 0, hasMore: false });
        return response.data.logs || [];
      });
    } catch (err) {
      console.error('Failed to load audit log:', err);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setExpandedRows(new Set()); // Collapse all rows when changing pages
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setPage(0); // Reset to first page when changing page size
    setExpandedRows(new Set());
  };

  const toggleRowExpanded = (logId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(logId)) {
      newExpanded.delete(logId);
    } else {
      newExpanded.add(logId);
    }
    setExpandedRows(newExpanded);
  };

  const handleViewLog = (log) => {
    setSelectedLog(log);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedLog(null);
  };

  const getActionTypeBadgeClass = (actionType) => {
    const typeMap = {
      'EDIT_PICK': 'action-edit',
      'EDIT_PICK_BEFORE': 'action-edit-before',
      'EDIT_PICK_AFTER': 'action-edit-after',
      'CREATE_PICK': 'action-create',
      'CREATE_PICK_BEFORE': 'action-create-before',
      'CREATE_PICK_AFTER': 'action-create-after',
      'USER_CREATED': 'action-user-create',
      'USER_UPDATED': 'action-user-update',
      'PASSWORD_RESET_SENT': 'action-password',
      'VERIFICATION_EMAIL_RESENT': 'action-email'
    };
    return typeMap[actionType] || 'action-default';
  };

  const formatActionType = (actionType) => {
    return actionType.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const renderJsonDiff = (oldValue, newValue) => {
    if (!oldValue && !newValue) return null;

    const oldObj = oldValue ? (typeof oldValue === 'string' ? JSON.parse(oldValue) : oldValue) : {};
    const newObj = newValue ? (typeof newValue === 'string' ? JSON.parse(newValue) : newValue) : {};

    const allKeys = new Set([...Object.keys(oldObj), ...Object.keys(newObj)]);

    return (
      <div className="json-diff">
        <table className="diff-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Old Value</th>
              <th>New Value</th>
            </tr>
          </thead>
          <tbody>
            {Array.from(allKeys).map(key => {
              const oldVal = oldObj[key];
              const newVal = newObj[key];
              const hasChanged = JSON.stringify(oldVal) !== JSON.stringify(newVal);

              return (
                <tr key={key} className={hasChanged ? 'changed' : ''}>
                  <td className="field-name">{key}</td>
                  <td className="old-value">{JSON.stringify(oldVal)}</td>
                  <td className="new-value">{JSON.stringify(newVal)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // Get unique action types for filter dropdown
  const actionTypes = ['all', ...new Set(logs.map(log => log.action_type))];

  if (loading) {
    return (
      <div className="audit-log-container">
        <div className="loading">Loading audit log...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="audit-log-container">
        <div className="error-container">
          <div className="error-message">{error}</div>
          <button onClick={loadAuditLog} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="audit-log-container">
      <div className="page-header">
        <button onClick={() => navigate('/picks')} className="btn-back">
          ← Back to Picks
        </button>
        <h1>Audit Log</h1>
        <div style={{ width: '120px' }}></div> {/* Spacer for alignment */}
      </div>

      <div className="controls-bar">
        <div className="search-section">
          <input
            type="text"
            className="search-input"
            placeholder="Search by admin, user, action, or reason..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="clear-search">
              Clear
            </button>
          )}
        </div>

        <div className="filter-section">
          <label htmlFor="action-filter">Action Type:</label>
          <select
            id="action-filter"
            className="action-filter"
            value={actionTypeFilter}
            onChange={(e) => setActionTypeFilter(e.target.value)}
          >
            {actionTypes.map(type => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Actions' : formatActionType(type)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="audit-table-container">
        <table className="audit-table">
          <thead>
            <tr>
              <th></th>
              <th>Date & Time</th>
              <th>Admin</th>
              <th>Action</th>
              <th>Target</th>
              <th className="hide-mobile">Entity</th>
              <th className="hide-mobile">IP Address</th>
              <th className="hide-mobile">Reason</th>
              <th className="view-mobile-header"></th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length === 0 ? (
              <tr>
                <td colSpan="9" className="no-results">
                  No audit log entries found
                </td>
              </tr>
            ) : (
              filteredLogs.map(log => (
                <React.Fragment key={log.id}>
                  <tr className={expandedRows.has(log.id) ? 'expanded' : ''}>
                    <td>
                      {(log.old_value || log.new_value) && (
                        <button
                          className="expand-btn"
                          onClick={() => toggleRowExpanded(log.id)}
                          aria-label={expandedRows.has(log.id) ? 'Collapse' : 'Expand'}
                        >
                          {expandedRows.has(log.id) ? '▼' : '▶'}
                        </button>
                      )}
                    </td>
                    <td className="datetime-cell">
                      {formatDateTime(log.created_at)}
                    </td>
                    <td className="admin-cell">
                      {log.admin_username}
                    </td>
                    <td>
                      <span className={`action-badge ${getActionTypeBadgeClass(log.action_type)}`}>
                        {formatActionType(log.action_type)}
                      </span>
                    </td>
                    <td className="target-cell">
                      {log.target_username || '—'}
                    </td>
                    <td className="entity-cell hide-mobile">
                      {log.entity_type ? (
                        <span>
                          {log.entity_type}
                          {log.entity_id && <span className="entity-id"> #{log.entity_id}</span>}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="ip-cell hide-mobile">
                      {log.ip_address || '—'}
                    </td>
                    <td className="reason-cell hide-mobile">
                      {log.reason || '—'}
                    </td>
                    <td className="view-mobile-cell">
                      <button
                        className="btn-view-mobile"
                        onClick={() => handleViewLog(log)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(log.id) && (
                    <tr className="details-row">
                      <td colSpan="9">
                        <div className="details-content">
                          <h4>Changes:</h4>
                          {renderJsonDiff(log.old_value, log.new_value)}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination-info">
          Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, pagination.total)} of {pagination.total} entries
          {filteredLogs.length < logs.length && ` (${filteredLogs.length} filtered)`}
        </div>

        <div className="pagination-controls">
          <label htmlFor="page-size">Entries per page:</label>
          <select
            id="page-size"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            className="page-size-select"
          >
            <option value="25">25</option>
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
          </select>

          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 0}
            className="btn-pagination"
          >
            Previous
          </button>

          <span className="page-indicator">
            Page {page + 1} of {Math.max(1, Math.ceil(pagination.total / pageSize))}
          </span>

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={!pagination.hasMore}
            className="btn-pagination"
          >
            Next
          </button>
        </div>
      </div>

      {showModal && selectedLog && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Audit Log Details</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">Date & Time:</span>
                <span className="detail-value">{formatDateTime(selectedLog.created_at)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Admin:</span>
                <span className="detail-value admin-cell">{selectedLog.admin_username}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Action:</span>
                <span className="detail-value">
                  <span className={`action-badge ${getActionTypeBadgeClass(selectedLog.action_type)}`}>
                    {formatActionType(selectedLog.action_type)}
                  </span>
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Target:</span>
                <span className="detail-value">{selectedLog.target_username || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Entity:</span>
                <span className="detail-value">
                  {selectedLog.entity_type ? (
                    <span>
                      {selectedLog.entity_type}
                      {selectedLog.entity_id && <span className="entity-id"> #{selectedLog.entity_id}</span>}
                    </span>
                  ) : '—'}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">IP Address:</span>
                <span className="detail-value ip-cell">{selectedLog.ip_address || '—'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Reason:</span>
                <span className="detail-value">{selectedLog.reason || '—'}</span>
              </div>
              {(selectedLog.old_value || selectedLog.new_value) && (
                <div className="detail-row detail-changes">
                  <span className="detail-label">Changes:</span>
                  <div className="detail-value">
                    {renderJsonDiff(selectedLog.old_value, selectedLog.new_value)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLog;
